import type { ApiResult, PaginatedResponse } from '@hypema/shared';

const API_URL = (import.meta.env.VITE_API_URL || '') + '/api';

function getHeaders(): HeadersInit {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('auth_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

function handleUnauthorized() {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('auth_token');
    window.dispatchEvent(new CustomEvent('session-expired'));
    setTimeout(() => { window.location.href = '/login'; }, 1500);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // AI calls (web search + adaptive thinking) can take several minutes.
  // Use 15 minutes for POST (AI calls), 60s for GET (data fetches).
  const timeoutMs = init?.method === 'POST' ? 900000 : 60000;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: { ...getHeaders(), ...init?.headers },
    signal: controller.signal,
  });
  clearTimeout(timeout);

  if (res.status === 401) {
    handleUnauthorized();
    throw new ApiRequestError('UNAUTHORIZED', 'Session expired. Please log in again.');
  }

  const json = await res.json();
  if (json.error) throw new ApiRequestError(json.error.code, json.error.message);
  return json.data;
}

export class ApiRequestError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
    this.name = 'ApiRequestError';
  }
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>(path);
}

/** For paginated endpoints that return { data: T[], total, page, pageSize } at the top level */
export async function apiGetPaginated<T>(path: string): Promise<{ data: T[]; total: number; page: number; pageSize: number }> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 60000);
  const res = await fetch(`${API_URL}${path}`, {
    headers: getHeaders(),
    signal: controller.signal,
  });
  clearTimeout(timeout);
  if (res.status === 401) {
    handleUnauthorized();
    throw new ApiRequestError('UNAUTHORIZED', 'Session expired. Please log in again.');
  }
  const json = await res.json();
  if (json.error) throw new ApiRequestError(json.error.code, json.error.message);
  return { data: json.data, total: json.total, page: json.page, pageSize: json.pageSize };
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
}

export function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}

/** Notify sidebar (and any other listener) that token usage changed. */
export function notifyTokensChanged() {
  window.dispatchEvent(new CustomEvent('tokens-changed'));
}

export function apiDelete<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: 'DELETE', body: body ? JSON.stringify(body) : undefined });
}

export async function apiUpload<T>(
  path: string,
  file: File,
  fieldName = 'file',
  extraFields?: Record<string, string>,
): Promise<T> {
  const formData = new FormData();
  formData.append(fieldName, file);
  if (extraFields) {
    for (const [key, value] of Object.entries(extraFields)) {
      formData.append(key, value);
    }
  }
  const headers: HeadersInit = {};
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('auth_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(`${API_URL}${path}`, { method: 'POST', headers, body: formData });
  if (res.status === 401) {
    handleUnauthorized();
    throw new ApiRequestError('UNAUTHORIZED', 'Session expired. Please log in again.');
  }
  const json = await res.json();
  if (json.error) throw new ApiRequestError(json.error.code, json.error.message);
  return json.data;
}
